import { FormEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, ClipboardList, Eye, Filter, Mail, PackageCheck, ReceiptText, RefreshCw, Search, Truck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminStoreSalesOrder,
  AdminStoreSalesOrderOption,
  AdminStoreSalesOrderStatus,
  AdminStoreUserLocation,
  AdminUser,
  PaginationMeta,
  autoRouteAdminStoreSalesOrder,
  getAdminStoreSalesOrder,
  getCurrentAdmin,
  listAdminStoreSalesOrders,
  logout,
  sendAdminStoreSalesOrderPickupNotification,
  updateAdminStoreSalesOrderStatus,
} from "./api";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };
const currentYear = new Date().getFullYear();
const defaultDateFrom = `${currentYear}-01-01`;
const defaultDateTo = `${currentYear}-12-31`;

const fallbackStatusOptions: AdminStoreSalesOrderOption[] = [
  { Key: "PendingHeadquarters", Label: "待總部確認" },
  { Key: "PendingEngineering", Label: "待工程部備貨" },
  { Key: "WaitingPurchase", Label: "待廠商採購" },
  { Key: "PreparingOutbound", Label: "待出庫" },
  { Key: "ShippedToStore", Label: "已出庫到店" },
  { Key: "ReadyForPickup", Label: "可通知取件" },
  { Key: "Completed", Label: "已交付" },
  { Key: "Cancelled", Label: "已取消" },
  { Key: "Voided", Label: "已作廢" },
];

const fallbackPaymentOptions: AdminStoreSalesOrderOption[] = [
  { Key: "Unpaid", Label: "未收款" },
  { Key: "DepositPaid", Label: "已收訂金" },
  { Key: "Paid", Label: "已結清" },
  { Key: "BalanceDue", Label: "尾款待收" },
];

const fallbackFulfillmentOptions: AdminStoreSalesOrderOption[] = [
  { Key: "Headquarters", Label: "總部出貨" },
  { Key: "StoreStock", Label: "門市現貨交付" },
];

const statusTone: Record<string, string> = {
  PendingHeadquarters: "bg-[#fff7e8] text-[#9a5b16]",
  PendingEngineering: "bg-[#eef3f8] text-[#255071]",
  WaitingPurchase: "bg-[#f6edf8] text-[#6b3b78]",
  PreparingOutbound: "bg-[#eaf4f4] text-[#255d60]",
  ShippedToStore: "bg-[#edf1fb] text-[#2e4d90]",
  ReadyForPickup: "bg-[#f1f8f3] text-[#245438]",
  Completed: "bg-[#e9f4ec] text-[#245438]",
  Cancelled: "bg-[#eeeeee] text-[#59635d]",
  Voided: "bg-[#fdebea] text-[#9a2d2d]",
};

const nextStatusHints: Record<string, AdminStoreSalesOrderStatus[]> = {
  PendingHeadquarters: ["PendingEngineering", "WaitingPurchase", "Cancelled"],
  PendingEngineering: ["PreparingOutbound", "WaitingPurchase", "Cancelled"],
  WaitingPurchase: ["PendingEngineering", "PreparingOutbound", "Cancelled"],
  PreparingOutbound: ["ShippedToStore", "Cancelled"],
  ShippedToStore: ["ReadyForPickup", "Completed"],
  ReadyForPickup: ["Completed"],
  StoreStock: ["Completed"],
};

function money(value: number) {
  return new Intl.NumberFormat("zh-TW", { style: "currency", currency: "TWD", maximumFractionDigits: 0 }).format(value || 0);
}

function errorText(error: unknown, fallback: string) {
  if (error instanceof AdminApiError) {
    const details = Object.values(error.errors || {}).filter(Boolean);
    return details.length ? `${error.message} ${details.join("、")}` : error.message;
  }
  return error instanceof Error ? error.message : fallback;
}

function canCompleteFromAdmin(order: AdminStoreSalesOrder) {
  if (order.BalanceAmount > 0) return "剩餘待付金額尚未結清，不能將訂單標記為已交付。請先由門市端建立收款紀錄，或確認付款資料已同步。";
  if (order.FulfillmentType !== "StoreStock" && !["ShippedToStore", "ReadyForPickup"].includes(order.Status)) {
    return "商品尚未到店，不能將總部出貨訂單標記為已交付。請先由 ERP 完成出庫到店，或改為可通知取件。";
  }
  return "";
}

export default function AdminStoreSalesOrders() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [orders, setOrders] = useState<AdminStoreSalesOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<AdminStoreSalesOrder | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [locations, setLocations] = useState<AdminStoreUserLocation[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>(fallbackStatusOptions);
  const [paymentOptions, setPaymentOptions] = useState<AdminStoreSalesOrderOption[]>(fallbackPaymentOptions);
  const [fulfillmentOptions, setFulfillmentOptions] = useState<AdminStoreSalesOrderOption[]>(fallbackFulfillmentOptions);
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [fulfillmentType, setFulfillmentType] = useState("");
  const [keyword, setKeyword] = useState("");
  const [filterLocationIds, setFilterLocationIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState<AdminStoreSalesOrderStatus>("PendingEngineering");
  const [internalNote, setInternalNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [routingStock, setRoutingStock] = useState(false);
  const [pageMessage, setPageMessage] = useState("");
  const [pageMessageTone, setPageMessageTone] = useState<"success" | "error">("error");

  const statusLabelMap = useMemo(() => Object.fromEntries(statusOptions.map((item) => [item.Key, item.Label])), [statusOptions]);
  const paymentLabelMap = useMemo(() => Object.fromEntries(paymentOptions.map((item) => [item.Key, item.Label])), [paymentOptions]);
  const fulfillmentLabelMap = useMemo(() => Object.fromEntries(fulfillmentOptions.map((item) => [item.Key, item.Label])), [fulfillmentOptions]);
  const locationLabelMap = useMemo(() => Object.fromEntries(locations.map((item) => [item.LocationId, item.Name])), [locations]);
  const filterSummary = useMemo(() => {
    const parts = [];
    if (status) parts.push(`流程：${statusLabelMap[status] || status}`);
    if (paymentStatus) parts.push(`收款：${paymentLabelMap[paymentStatus] || paymentStatus}`);
    if (fulfillmentType) parts.push(`交付：${fulfillmentLabelMap[fulfillmentType] || fulfillmentType}`);
    if (keyword.trim()) parts.push(`關鍵字：${keyword.trim()}`);
    if (dateFrom !== defaultDateFrom || dateTo !== defaultDateTo) parts.push(`日期：${dateFrom || "-"} ~ ${dateTo || "-"}`);
    if (filterLocationIds.length > 0) {
      const locationText = filterLocationIds.slice(0, 2).map((id) => locationLabelMap[id] || id).join("、");
      parts.push(`門市：${locationText}${filterLocationIds.length > 2 ? ` 等 ${filterLocationIds.length} 間` : ""}`);
    }
    return parts.length ? parts.join(" / ") : "預設：今年全部門市訂單";
  }, [dateFrom, dateTo, filterLocationIds, fulfillmentLabelMap, fulfillmentType, keyword, locationLabelMap, paymentLabelMap, paymentStatus, status, statusLabelMap]);
  const visibleNextStatuses = useMemo(() => {
    if (!selectedOrder) return fallbackStatusOptions;
    if (selectedOrder.Status === "Voided" || selectedOrder.Status === "Cancelled" || selectedOrder.Status === "Completed") return [];
    if (selectedOrder.NextStatusOptions?.length) return selectedOrder.NextStatusOptions;
    const keys = selectedOrder.FulfillmentType === "StoreStock" ? nextStatusHints.StoreStock : nextStatusHints[selectedOrder.Status];
    return (keys || []).map((key) => ({ Key: key, Label: statusLabelMap[key] || key }));
  }, [selectedOrder, statusLabelMap]);
  const pickupNotification = useMemo(
    () => selectedOrder?.Notifications?.find((item) => item.NotificationType === "PickupReady" && item.Method === "Email" && item.Status === "Sent") || null,
    [selectedOrder],
  );
  const pickupNotificationCount = useMemo(
    () => (selectedOrder?.Notifications || []).filter((item) => item.NotificationType === "PickupReady" && item.Method === "Email" && item.Status === "Sent").length,
    [selectedOrder],
  );

  async function load(
    filters = { Status: status, PaymentStatus: paymentStatus, FulfillmentType: fulfillmentType, Keyword: keyword, LocationIds: filterLocationIds, DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage },
    options: { clearMessage?: boolean; preserveSelection?: boolean } = {},
  ) {
    const { clearMessage = true, preserveSelection = false } = options;
    setLoading(true);
    if (clearMessage) {
      setPageMessage("");
      setPageMessageTone("error");
    }
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminStoreSalesOrders(filters);
      setOrders(data.Orders || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      setLocations(data.Locations || []);
      setStatusOptions(data.StatusOptions?.length ? data.StatusOptions : fallbackStatusOptions);
      setPaymentOptions(data.PaymentStatusOptions?.length ? data.PaymentStatusOptions : fallbackPaymentOptions);
      setFulfillmentOptions(data.FulfillmentTypeOptions?.length ? data.FulfillmentTypeOptions : fallbackFulfillmentOptions);
      if (!preserveSelection && selectedOrder && !data.Orders.some((item) => item.StoreSalesOrderId === selectedOrder.StoreSalesOrderId)) {
        setSelectedOrder(null);
      }
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "載入失敗，請稍後再試。");
      setPageMessageTone("error");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryStatus = params.get("Status") || "";
    const queryPaymentStatus = params.get("PaymentStatus") || "";
    const queryFulfillmentType = params.get("FulfillmentType") || "";
    const queryKeyword = (params.get("Keyword") || "").trim();
    const queryLocationIds = [...params.getAll("LocationIds[]"), ...params.getAll("LocationIds")].map((id) => id.trim()).filter(Boolean);
    const queryDateFrom = /^\d{4}-\d{2}-\d{2}$/.test(params.get("DateFrom") || "") ? params.get("DateFrom") || defaultDateFrom : defaultDateFrom;
    const queryDateTo = /^\d{4}-\d{2}-\d{2}$/.test(params.get("DateTo") || "") ? params.get("DateTo") || defaultDateTo : defaultDateTo;
    const queryPage = Math.max(1, Number(params.get("Page")) || 1);

    setStatus(queryStatus);
    setPaymentStatus(queryPaymentStatus);
    setFulfillmentType(queryFulfillmentType);
    setKeyword(queryKeyword);
    setFilterLocationIds(queryLocationIds);
    setDateFrom(queryDateFrom);
    setDateTo(queryDateTo);
    setFiltersOpen(false);
    load({ Status: queryStatus, PaymentStatus: queryPaymentStatus, FulfillmentType: queryFulfillmentType, Keyword: queryKeyword, LocationIds: queryLocationIds, DateFrom: queryDateFrom, DateTo: queryDateTo, Page: queryPage, PerPage: defaultPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!selectedOrder) return;
    const next = visibleNextStatuses[0]?.Key as AdminStoreSalesOrderStatus | undefined;
    if (next) setTargetStatus(next);
  }, [selectedOrder, visibleNextStatuses]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  async function openDetail(orderId: string) {
    setDetailLoading(true);
    setPageMessage("");
    setPageMessageTone("error");
    try {
      const data = await getAdminStoreSalesOrder(orderId);
      setSelectedOrder(data.Order);
      setInternalNote(data.Order.InternalNote || "");
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "讀取明細失敗。");
      setPageMessageTone("error");
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleStatusUpdate() {
    if (!selectedOrder || !targetStatus) return;
    if (targetStatus === "Completed") {
      const invalidReason = canCompleteFromAdmin(selectedOrder);
      if (invalidReason) {
        setPageMessage(invalidReason);
        setPageMessageTone("error");
        return;
      }
    }
    setSaving(true);
    setPageMessage("");
    setPageMessageTone("error");
    try {
      const result = await updateAdminStoreSalesOrderStatus(selectedOrder.StoreSalesOrderId, { Status: targetStatus, InternalNote: internalNote });
      setSelectedOrder(result.Order);
      setInternalNote(result.Order.InternalNote || "");
      setPageMessage(result.message || "門市訂單流程狀態已更新。");
      setPageMessageTone("success");
      load({ Status: status, PaymentStatus: paymentStatus, FulfillmentType: fulfillmentType, Keyword: keyword.trim(), LocationIds: filterLocationIds, DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage }, { clearMessage: false, preserveSelection: true }).catch((error) => {
        setPageMessage(`流程狀態已儲存，但列表重新整理失敗：${errorText(error, "請稍後再查詢。")}`);
        setPageMessageTone("success");
      });
    } catch (error) {
      setPageMessage(errorText(error, "狀態更新失敗。"));
      setPageMessageTone("error");
    } finally {
      setSaving(false);
    }
  }

  async function handleAutoRoute() {
    if (!selectedOrder) return;
    setRoutingStock(true);
    setPageMessage("");
    setPageMessageTone("error");
    try {
      const result = await autoRouteAdminStoreSalesOrder(selectedOrder.StoreSalesOrderId, { InternalNote: internalNote });
      setSelectedOrder(result.Order);
      setInternalNote(result.Order.InternalNote || "");
      setPageMessage(result.message || "已依 ERP 庫存完成分流。");
      setPageMessageTone("success");
      load({ Status: status, PaymentStatus: paymentStatus, FulfillmentType: fulfillmentType, Keyword: keyword.trim(), LocationIds: filterLocationIds, DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage }, { clearMessage: false, preserveSelection: true }).catch((error) => {
        setPageMessage(`分流已儲存，但列表重新整理失敗：${errorText(error, "請稍後再查詢。")}`);
        setPageMessageTone("success");
      });
    } catch (error) {
      setPageMessage(errorText(error, "自動庫存分流失敗。"));
      setPageMessageTone("error");
    } finally {
      setRoutingStock(false);
    }
  }

  async function handleSendPickupNotification() {
    if (!selectedOrder) return;
    setSendingNotification(true);
    setPageMessage("");
    setPageMessageTone("error");
    try {
      const result = await sendAdminStoreSalesOrderPickupNotification(selectedOrder.StoreSalesOrderId);
      setSelectedOrder(result.Order);
      setPageMessage(result.message || "取件通知已寄送。");
      setPageMessageTone("success");
      load({ Status: status, PaymentStatus: paymentStatus, FulfillmentType: fulfillmentType, Keyword: keyword.trim(), LocationIds: filterLocationIds, DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage }, { clearMessage: false, preserveSelection: true }).catch((error) => {
        setPageMessage(`取件通知已寄送，但列表重新整理失敗：${errorText(error, "請稍後再查詢。")}`);
        setPageMessageTone("success");
      });
    } catch (error) {
      setPageMessage(errorText(error, "取件通知寄送失敗。"));
      setPageMessageTone("error");
    } finally {
      setSendingNotification(false);
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, PaymentStatus: paymentStatus, FulfillmentType: fulfillmentType, Keyword: keyword.trim(), LocationIds: filterLocationIds, DateFrom: dateFrom, DateTo: dateTo, Page: 1, PerPage: pagination.PerPage });
    setFiltersOpen(false);
  }

  function resetFilters() {
    setStatus("");
    setPaymentStatus("");
    setFulfillmentType("");
    setKeyword("");
    setFilterLocationIds([]);
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setFiltersOpen(false);
    load({ Status: "", PaymentStatus: "", FulfillmentType: "", Keyword: "", LocationIds: [], DateFrom: defaultDateFrom, DateTo: defaultDateTo, Page: 1, PerPage: pagination.PerPage });
  }

  function toggleFilterLocation(LocationId: string) {
    setFilterLocationIds((current) => (current.includes(LocationId) ? current.filter((id) => id !== LocationId) : [...current, LocationId]));
  }

  return (
    <AdminLayout sectionLabel="門市管理" title="門市訂單管理" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-3 2xl:p-4" onSubmit={handleSearch}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-extrabold text-[#203d32]"><Filter size={16} />篩選條件</div>
            <p className="mt-1 break-words text-sm font-bold leading-6 text-[#7b877f]">{filterSummary}</p>
          </div>
          <button className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFiltersOpen((current) => !current)} type="button">
            {filtersOpen ? "收合篩選" : "展開篩選"}
          </button>
        </div>
        {filtersOpen ? <div className="mt-3 grid gap-3 border-t border-[#edf0ee] pt-3 sm:grid-cols-2 2xl:grid-cols-[150px_150px_150px_minmax(300px,1fr)_190px_190px_auto] 2xl:items-end">
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-sales-status">流程狀態</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-sales-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            {statusOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
          </select>
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-sales-payment">收款狀態</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-sales-payment" value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value)}>
            <option value="">全部</option>
            {paymentOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
          </select>
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-sales-fulfillment">交付方式</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-sales-fulfillment" value={fulfillmentType} onChange={(event) => setFulfillmentType(event.target.value)}>
            <option value="">全部</option>
            {fulfillmentOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
          </select>
        </div>
        <div className="grid min-w-0 gap-1 sm:col-span-2 2xl:col-span-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-sales-keyword">關鍵字</label>
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-sales-keyword" placeholder="單號、客戶、電話、Email" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <div className="grid min-w-0 gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-sales-date-from">起日</label>
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-sales-date-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
        </div>
        <div className="grid min-w-0 gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-sales-date-to">迄日</label>
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-sales-date-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
        </div>
        <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] xl:px-5" type="submit">
            <Search size={18} />查詢
          </button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={resetFilters} type="button">
            <RefreshCw size={18} />重設
          </button>
        </div>
        <div className="border-t border-[#edf0ee] pt-3 sm:col-span-2 2xl:col-span-7">
          <div className="mb-2 grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-between">
            <label className="inline-flex items-center gap-2 text-sm font-bold text-[#59665f]"><Filter size={15} />門市篩選</label>
            <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-[#315746]">
              <span className="text-[#7b877f]">已選 {filterLocationIds.length} 間</span>
              <button className="hover:underline" onClick={() => setFilterLocationIds(locations.map((item) => item.LocationId))} type="button">全選</button>
              <span className="text-[#c9d2cc]">|</span>
              <button className="hover:underline" onClick={() => setFilterLocationIds([])} type="button">清除</button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
            {locations.map((location) => {
              const checked = filterLocationIds.includes(location.LocationId);
              return (
                <label className={`flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-bold ${checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#245438]" : "border-[#d8dedb] bg-white text-[#59665f]"}`} key={location.LocationId}>
                  <input checked={checked} className="h-4 w-4 accent-[#315746]" onChange={() => toggleFilterLocation(location.LocationId)} type="checkbox" />
                  {location.Name}
                </label>
              );
            })}
          </div>
        </div>
        </div> : null}
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <SummaryCard icon={<ClipboardList size={18} />} label="全部訂單" value={counts.Total || 0} />
        <SummaryCard icon={<AlertTriangle size={18} />} label="待總部確認" value={counts.PendingHeadquarters || 0} />
        <SummaryCard icon={<PackageCheck size={18} />} label="待工程/採購" value={(counts.PendingEngineering || 0) + (counts.WaitingPurchase || 0)} />
        <SummaryCard icon={<Truck size={18} />} label="待出庫/到店" value={(counts.PreparingOutbound || 0) + (counts.ShippedToStore || 0)} />
        <SummaryCard icon={<CheckCircle2 size={18} />} label="已交付" value={counts.Completed || 0} />
      </div>

      {pageMessage && <div className={`mb-4 rounded-md border px-4 py-3 text-sm font-medium ${pageMessageTone === "success" ? "border-[#cfe3d5] bg-[#f2f9f4] text-[#245438]" : "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]"}`}>{pageMessage}</div>}

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.25fr)_minmax(380px,.75fr)]">
        <div className="min-w-0 rounded-md border border-[#dfe6e1] bg-white">
          <div className="max-h-[68vh] overflow-auto">
            <table className="w-full min-w-[1220px] table-fixed border-separate border-spacing-0 text-left">
              <thead className="sticky top-0 z-40 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
                <tr>
                  <th className="w-[240px] whitespace-nowrap px-4 py-3 font-bold">訂單</th>
                  <th className="w-[260px] whitespace-nowrap px-4 py-3 font-bold">客戶</th>
                  <th className="w-[280px] whitespace-nowrap px-4 py-3 font-bold">商品</th>
                  <th className="w-[190px] whitespace-nowrap px-4 py-3 font-bold">金額 / 付款</th>
                  <th className="sticky right-0 z-50 w-[200px] whitespace-nowrap bg-[#f0f3f1] px-4 py-3 text-right font-bold shadow-[-10px_0_18px_rgba(32,61,50,.08)]">流程 / 操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>載入中...</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>目前沒有符合條件的門市訂單。</td></tr>
                ) : orders.map((order) => (
                  <tr className={`border-t border-[#edf0ee] align-top transition hover:bg-[#f7faf8] ${selectedOrder?.StoreSalesOrderId === order.StoreSalesOrderId ? "bg-[#f1f8f3]" : "bg-white"}`} key={order.StoreSalesOrderId}>
                    <td className="px-4 py-4">
                      <div className="whitespace-nowrap text-base font-bold text-[#203d32]">{order.OrderNumber}</div>
                      <div className="mt-1 whitespace-nowrap text-xs font-medium text-[#7b877f]">{order.CreatedAt || "-"}</div>
                      <div className="mt-2 text-xs font-bold leading-5 text-[#59665f]">{order.LocationName || "-"} / {order.StoreUserName || "未記錄"}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="break-words font-bold text-[#203d32]">{order.CustomerName}</div>
                      <div className="mt-1 whitespace-nowrap text-sm leading-6 text-[#59665f]">{order.CustomerPhone}</div>
                      {order.CustomerEmail && <div className="break-all text-sm leading-6 text-[#59665f]">{order.CustomerEmail}</div>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="break-words text-sm font-bold leading-6 text-[#203d32]">{order.FirstProductName || "-"}</div>
                      <div className="mt-1 text-sm leading-6 text-[#59665f]">{order.FirstVariantLabel || ""}{order.ItemCount ? ` / ${order.ItemCount} 項` : ""}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="whitespace-nowrap font-bold text-[#203d32]">{money(order.TotalAmount)}</div>
                      <div className="mt-1 whitespace-nowrap text-sm leading-6 text-[#59665f]">訂金 {money(order.DepositAmount)}</div>
                      <div className="whitespace-nowrap text-sm leading-6 text-[#59665f]">剩餘待付 {money(order.BalanceAmount)}</div>
                      <div className="mt-1 text-xs font-bold text-[#59665f]">{order.PaymentStatusLabel}</div>
                    </td>
                    <td className="sticky right-0 z-10 bg-white px-4 py-4 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">
                      <div className="flex flex-col items-end gap-2">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusTone[order.Status] || "bg-[#eeeeee] text-[#59635d]"}`}>{order.StatusLabel}</span>
                        <div className="text-xs font-bold text-[#59665f]">{order.FulfillmentTypeLabel}</div>
                        <button className="inline-flex h-9 min-w-[82px] items-center justify-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openDetail(order.StoreSalesOrderId)} type="button">
                          <Eye size={16} />查看
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, PaymentStatus: paymentStatus, FulfillmentType: fulfillmentType, Keyword: keyword.trim(), LocationIds: filterLocationIds, DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: pagination.PerPage })} />
        </div>

        <aside className="rounded-md border border-[#dfe6e1] bg-white">
          {!selectedOrder ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center text-[#59665f]">
              <ClipboardList className="mb-3 text-[#9aa8a0]" size={34} />
              <p className="font-bold text-[#203d32]">選擇一張訂單</p>
              <p className="mt-2 text-sm leading-6">總管理端在這裡接收門市訂單，先完成總部確認與狀態分流；庫存、採購、工程出貨會留給 ERP 階段。</p>
            </div>
          ) : (
            <div className="grid gap-4 p-4">
              <div className="border-b border-[#edf0ee] pb-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold tracking-[.16em] text-[#7b877f]">STORE SALES ORDER</p>
                    <h2 className="mt-1 text-xl font-bold text-[#203d32]">{selectedOrder.OrderNumber}</h2>
                  </div>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusTone[selectedOrder.Status] || "bg-[#eeeeee] text-[#59635d]"}`}>{selectedOrder.StatusLabel}</span>
                </div>
                {detailLoading && <p className="mt-3 text-sm text-[#59665f]">明細載入中...</p>}
              </div>

              <div className="grid gap-3 text-sm">
                <InfoRow label="門市" value={selectedOrder.LocationName || "-"} />
                <InfoRow label="建立人" value={selectedOrder.StoreUserName || "-"} />
                <InfoRow label="客戶" value={`${selectedOrder.CustomerName} / ${selectedOrder.CustomerPhone}`} />
                <InfoRow label="Email" value={selectedOrder.CustomerEmail || "-"} wrap />
                <InfoRow label="交付方式" value={selectedOrder.FulfillmentTypeLabel} />
                <InfoRow label="訂單性質" value={selectedOrder.OrderPurposeLabel || selectedOrder.OrderPurpose || "一般銷售"} />
                {(selectedOrder.ZeroPriceReason || selectedOrder.ZeroPriceNote) && (
                  <InfoRow label="0 元原因" value={[selectedOrder.ZeroPriceReasonLabel || selectedOrder.ZeroPriceReason, selectedOrder.ZeroPriceNote].filter(Boolean).join(" / ")} wrap />
                )}
                <InfoRow label="預計取件" value={selectedOrder.ExpectedPickupDate || "-"} />
              </div>

              <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
                <p className="mb-2 font-bold text-[#203d32]">商品明細</p>
                <div className="grid gap-2">
                  {(selectedOrder.Items || []).map((item) => (
                    <div className="rounded-md border border-[#e5ebe7] bg-white p-3" key={item.StoreSalesOrderItemId}>
                      <div className="font-bold text-[#203d32]">{item.ProductName}{item.VariantLabel ? ` / ${item.VariantLabel}` : ""}</div>
                      <div className="mt-1 grid gap-1 text-xs text-[#59665f]">
                        <span>SKU：{item.Sku || "-"}</span>
                        <span>成交價：{money(item.DealPrice)} × {item.Quantity}</span>
                        <span>序號：{item.SerialNumber || "-"}</span>
                        <span>保固起日：{item.WarrantyStartDate || "-"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-2 rounded-md border border-[#dfe6e1] bg-white p-3">
                <p className="font-bold text-[#203d32]">金額</p>
                <InfoRow label="成交總額" value={money(selectedOrder.TotalAmount)} />
                <InfoRow label="已收金額" value={money(selectedOrder.DepositAmount)} />
                <InfoRow label="剩餘待付" value={money(selectedOrder.BalanceAmount)} />
                <InfoRow label="收款狀態" value={selectedOrder.PaymentStatusLabel} />
              </div>

              <div className="grid gap-2 rounded-md border border-[#dfe6e1] bg-white p-3">
                <p className="font-bold text-[#203d32]">發票資訊</p>
                <InfoRow label="發票類型" value={selectedOrder.InvoiceTypeLabel || selectedOrder.InvoiceType || "-"} />
                <InfoRow label="發票狀態" value={selectedOrder.InvoiceStatusLabel || selectedOrder.InvoiceStatus || "-"} />
                <InfoRow label="統一編號" value={selectedOrder.InvoiceTaxId || "-"} />
                <InfoRow label="發票抬頭" value={selectedOrder.InvoiceTitle || "-"} wrap />
                <InfoRow label="載具號碼" value={selectedOrder.InvoiceCarrier || "-"} wrap />
                <InfoRow label="捐贈碼" value={selectedOrder.InvoiceDonationCode || "-"} />
                <InfoRow label="備註" value={selectedOrder.InvoiceNote || "-"} wrap />
              </div>

              <div className="grid gap-2 rounded-md border border-[#dfe6e1] bg-white p-3">
                <div className="flex items-center gap-2 font-bold text-[#203d32]"><ReceiptText size={17} />收款紀錄</div>
                {(selectedOrder.Payments || []).length === 0 ? (
                  <p className="text-sm text-[#7b877f]">尚無收款流水。</p>
                ) : (
                  <div className="grid gap-2">
                    {(selectedOrder.Payments || []).map((item) => (
                      <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-2 text-sm" key={item.StoreSalesOrderPaymentId}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-[#203d32]">{item.PaymentTypeLabel || item.PaymentType}</span>
                          <span className="font-extrabold text-[#203d32]">{money(item.Amount)}</span>
                        </div>
                        <p className="mt-1 text-xs font-bold text-[#7b877f]">{item.PaymentMethodLabel || item.PaymentMethod} / {item.StoreUserName || "未記錄"} / {item.ReceivedAt || "-"}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-3 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
                <div className="flex items-center gap-2 font-bold text-[#203d32]"><Mail size={17} />取件通知</div>
                {pickupNotification ? (
                  <div className="rounded-md border border-[#cfe3d5] bg-[#f2f9f4] px-3 py-2 text-sm font-bold text-[#245438]">
                    最近一次於 {pickupNotification.SentAt || pickupNotification.CreatedAt || "-"} 由 {pickupNotification.AdminUserName || pickupNotification.StoreUserName || "系統人員"} 寄送至 {pickupNotification.RecipientEmail}
                    {pickupNotificationCount > 1 ? `，共寄送 ${pickupNotificationCount} 次。` : "。"}
                  </div>
                ) : (
                  <p className="text-sm leading-6 text-[#59665f]">訂單到店後可寄送正式 Email，系統會記錄寄送時間並建立 7 天未取件回訪提醒。</p>
                )}
                <button
                  className="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  disabled={sendingNotification || !selectedOrder.CustomerEmail || !["ShippedToStore", "ReadyForPickup"].includes(selectedOrder.Status)}
                  onClick={handleSendPickupNotification}
                  type="button"
                >
                  <Mail size={18} />{sendingNotification ? "寄送中" : pickupNotification ? "再次寄送通知" : "寄送取件通知"}
                </button>
                {!selectedOrder.CustomerEmail && <p className="text-xs font-bold text-[#9a2d2d]">此訂單沒有 Email，請先由門市補齊客戶 Email。</p>}
              </div>

              <div className="grid gap-3 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
                <p className="font-bold text-[#203d32]">總部處理</p>
                {selectedOrder.StockDecision && (
                  <div className="rounded-md border border-[#cfe3d5] bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-bold text-[#203d32]">{selectedOrder.StockDecision.Label}</p>
                      <span className="text-xs font-bold text-[#7b877f]">{selectedOrder.StockDecision.CheckedAt || "尚未記錄時間"}</span>
                    </div>
                    {selectedOrder.StockDecision.Summary && <p className="mt-2 text-sm font-medium leading-6 text-[#59665f]">{selectedOrder.StockDecision.Summary}</p>}
                    <div className="mt-3 grid gap-2">
                      {(selectedOrder.StockDecision.Items || []).map((item, index) => (
                        <div className="grid gap-2 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-2 text-sm sm:grid-cols-[minmax(0,1fr)_auto]" key={`${item.Sku || item.ProductName}-${index}`}>
                          <div className="min-w-0">
                            <p className="break-words font-bold text-[#203d32]">{item.ProductName}{item.VariantLabel ? ` / ${item.VariantLabel}` : ""}</p>
                            <p className="break-all text-xs font-bold text-[#7b877f]">{item.Sku || "-"}</p>
                          </div>
                          <p className="font-bold text-[#59665f]">需求 {item.RequiredQuantity} / 可用 {item.AvailableQuantity} / 缺口 {item.ShortageQuantity}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {visibleNextStatuses.length === 0 ? (
                  <p className="text-sm text-[#59665f]">此訂單已在終態，若需重開或逆轉流程，建議後續由稽核權限或 ERP 流程處理。</p>
                ) : (
                  <>
                    <button className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={routingStock || !["PendingHeadquarters", "PendingEngineering", "WaitingPurchase"].includes(selectedOrder.Status)} onClick={handleAutoRoute} type="button">
                      <PackageCheck size={18} />{routingStock ? "判斷中" : "自動判斷庫存並分流"}
                    </button>
                    <label className="grid gap-1 text-sm font-bold text-[#315746]">
                      下一步狀態
                      <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-medium outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={targetStatus} onChange={(event) => setTargetStatus(event.target.value as AdminStoreSalesOrderStatus)}>
                        {visibleNextStatuses.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1 text-sm font-bold text-[#315746]">
                      內部備註
                      <textarea className="min-h-[92px] rounded-md border border-[#cfd8d2] px-3 py-2 font-medium outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={4000} value={internalNote} onChange={(event) => setInternalNote(event.target.value)} />
                    </label>
                    <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" disabled={saving} onClick={handleStatusUpdate} type="button">
                      <CheckCircle2 size={18} />{saving ? "更新中" : "更新流程狀態"}
                    </button>
                  </>
                )}
              </div>

              {(selectedOrder.CustomerNote || selectedOrder.InternalNote || selectedOrder.DeliveryNote || selectedOrder.VoidReason) && (
                <div className="grid gap-2 rounded-md border border-[#dfe6e1] bg-white p-3 text-sm">
                  <p className="font-bold text-[#203d32]">備註</p>
                  {selectedOrder.CustomerNote && <InfoRow label="客戶備註" value={selectedOrder.CustomerNote} wrap />}
                  {selectedOrder.InternalNote && <InfoRow label="內部備註" value={selectedOrder.InternalNote} wrap />}
                  {selectedOrder.DeliveryNote && <InfoRow label="交付備註" value={selectedOrder.DeliveryNote} wrap />}
                  {selectedOrder.VoidReason && <InfoRow label="作廢原因" value={selectedOrder.VoidReason} wrap />}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold text-[#7b877f]">{label}</p>
        <span className="text-[#315746]">{icon}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-[#203d32]">{value}</p>
    </div>
  );
}

function InfoRow({ label, value, wrap = false }: { label: string; value: string; wrap?: boolean }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[88px_1fr] sm:gap-3">
      <span className="font-bold text-[#7b877f]">{label}</span>
      <span className={`font-bold text-[#203d32] ${wrap ? "min-w-0 break-words" : ""}`}>{value}</span>
    </div>
  );
}
